/**
 * Retargeting DataLayer Events
 * Pushes segmented events to window.dataLayer for Google Ads remarketing audiences
 * These allow creating audience segments in Google Ads based on on-site behavior
 */

const pushEvent = (eventName: string, params?: Record<string, unknown>) => {
  if (typeof window === 'undefined') return;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const w = window as any;
  w.dataLayer = w.dataLayer || [];
  w.dataLayer.push({
    event: eventName,
    ...params,
  });
};

// ---- Audience Segment Events ---- //

/** Fired when user completes the solar calculator */
export const trackCalculatorComplete = (estimatedSavings?: number) => {
  pushEvent('calculator_complete', {
    estimated_savings: estimatedSavings,
    audience_segment: 'calculator_user',
  });
};

/** Fired when user views 3+ blog posts in a session */
export const trackBlogEngagement = (postCount: number) => {
  if (postCount >= 3) {
    pushEvent('blog_engaged', {
      posts_viewed: postCount,
      audience_segment: 'blog_researcher',
    });
  }
};

/** Fired when user scrolls past 75% of homepage */
export const trackDeepScroll = (scrollPercent: number) => {
  if (scrollPercent >= 75) {
    pushEvent('deep_scroll', {
      scroll_depth: scrollPercent,
      audience_segment: 'engaged_visitor',
    });
  }
};

/** Fired when user is a return visitor */
export const trackReturnVisitor = (visitCount: number) => {
  pushEvent('return_visitor', {
    visit_count: visitCount,
    audience_segment: 'return_visitor',
  });
};

/** Fired when user views equipment/product pages */
export const trackEquipmentInterest = (equipment: string) => {
  pushEvent('equipment_viewed', {
    equipment_type: equipment,
    audience_segment: 'equipment_researcher',
  });
};

/** Fired when user clicks CTA but doesn't convert */
export const trackCTAClickNoConvert = (ctaType: string) => {
  pushEvent('cta_click_no_convert', {
    cta_type: ctaType,
    audience_segment: 'hot_prospect',
  });
};

/** Fired on high engagement score (composite signal) */
export const trackHighEngagement = (score: number) => {
  if (score >= 50) {
    pushEvent('high_engagement', {
      engagement_score: score,
      audience_segment: 'high_intent',
    });
  }
};

/** Fired when user views the scheduling calendar */
export const trackCalendarView = () => {
  pushEvent('calendar_viewed', {
    audience_segment: 'scheduling_intent',
  });
};

/** Fired when user spends 3+ minutes on site */
export const trackTimeEngagement = (seconds: number) => {
  if (seconds >= 180) {
    pushEvent('time_engaged', {
      time_on_site_seconds: seconds,
      audience_segment: 'time_engaged',
    });
  }
};
